import React, { useState, useEffect } from 'react'
import Checkbox from '@chegg-wt/core/components/Checkbox'
import {
  getContributors,
  getCitationTitle,
  getCitationYear,
  getCitationIdentifier,
  getStyleUrl,
} from '@chegg-wt/core/utils/citations'
import {
  keepTextContent,
  sanitizeHtmlWithFilter,
} from '@chegg-wt/core/utils/contentFilters'
import { formatInTextPreview } from '@chegg-wt/core/utils/previewCitation'
import { Clipboard } from '@chegg-wt/core/utils/clipboardPolyfill'
import { Citation } from '@chegg-wt/core/types/redux-citation-api'
import * as Styled from './styled'
import { PREMIUM_COPY_MODAL_INTEXT_CONTAINER_ID } from './testIds'

const inTextInitialState = {
  inTextPreview: '()',
}

interface Props {
  citation: Citation
  style: string
}

const InTextModal: React.FC<Props> = ({ citation, style }) => {
  // selectors
  const styleUrl = getStyleUrl(style)
  const isMLA = style.startsWith('mla')
  const isHarvardCiteThemRight10 = style === 'harvard-cite-them-right-10'

  // get pre-generated input fields data
  const contributors = getContributors(citation, style)
  const title = getCitationTitle(citation)
  const year = getCitationYear(citation, style)
  const identifiers = getCitationIdentifier(citation, style)

  // in text modal state
  const [contributorsValue, setContributors] = useState(contributors)
  const [titleValue, setTitle] = useState(title)
  const [yearValue, setYear] = useState(year)
  const [identifiersValue, setIdentifiers] = useState(identifiers)
  const [contributorsSelected, toggleContributorsSelected] = useState(!!contributors)
  const [titleSelected, toggleTitleSelected] = useState(false)
  const [yearSelected, toggleYearSelected] = useState(!!year)
  const [identifiersSelected, toggleidentifiersSelected] = useState(!!identifiers)
  const [inTextPreview, setInTextPreview] = useState(inTextInitialState.inTextPreview)
  const [copied, setCopied] = useState(false)

  const handleCopy = (html) => {
    Clipboard.copy({
      'text/plain': keepTextContent(html),
      'text/html': sanitizeHtmlWithFilter(html),
    })
    setCopied(true)
  }

  const handleInputChange = () => {
    const preview = formatInTextPreview({
      contributorsSelected,
      contributorsValue,
      isMLA,
      titleSelected,
      titleValue,
      yearSelected,
      yearValue,
      identifiersSelected,
      identifiersValue,
      isHarvardCiteThemRight10,
    })
    return setInTextPreview(preview)
  }

  // handle input when state changes
  useEffect(() => {
    handleInputChange()
  }, [
    contributorsSelected,
    contributorsValue,
    titleSelected,
    titleValue,
    yearSelected,
    yearValue,
    identifiersSelected,
    identifiersValue,
  ])

  return (
    <div data-test={PREMIUM_COPY_MODAL_INTEXT_CONTAINER_ID}>
      <Styled.FormContainer data-test="ITC-modal">
        <Styled.InputContainer>
          <Styled.CheckboxField>
            <Checkbox
              id="contributors"
              selected={contributorsSelected}
              onChange={() => {
                toggleContributorsSelected(!contributorsSelected)
              }}
              label="select contributors"
            />
          </Styled.CheckboxField>
          <Styled.TextField>
            <Styled.SubHeader>Contributors</Styled.SubHeader>
            <Styled.Input
              type="text"
              data-test="in-text-contributors-input"
              value={contributorsValue}
              onChange={(e) => {
                setContributors(e.target.value)
              }}
            />
          </Styled.TextField>
        </Styled.InputContainer>
        <Styled.InputContainer>
          <Styled.CheckboxField>
            <Checkbox
              id="title"
              selected={titleSelected}
              onChange={() => {
                toggleTitleSelected(!titleSelected)
              }}
              label="select title"
            />
          </Styled.CheckboxField>
          <Styled.TextField>
            <Styled.SubHeader>Title</Styled.SubHeader>
            <Styled.Input
              type="text"
              data-test="in-text-title-input"
              value={titleValue}
              onChange={(e) => {
                setTitle(e.target.value)
              }}
            />
          </Styled.TextField>
        </Styled.InputContainer>
        {!isMLA && (
          <Styled.InputContainer>
            <Styled.CheckboxField>
              <Checkbox
                id="year"
                selected={yearSelected}
                onChange={() => {
                  toggleYearSelected(!yearSelected)
                }}
                label="select year"
              />
            </Styled.CheckboxField>
            <Styled.TextField>
              <Styled.SubHeader>Year</Styled.SubHeader>
              <Styled.Input
                type="text"
                data-test="in-text-year-input"
                value={yearValue}
                onChange={(e) => {
                  setYear(e.target.value)
                }}
              />
            </Styled.TextField>
          </Styled.InputContainer>
        )}
        <Styled.InputContainer>
          <Styled.CheckboxField>
            <Checkbox
              id="identifier"
              selected={identifiersSelected}
              onChange={() => {
                toggleidentifiersSelected(!identifiersSelected)
              }}
              label="select identifiers"
            />
          </Styled.CheckboxField>
          <Styled.TextField>
            <Styled.SubHeader>Identifiers</Styled.SubHeader>
            <Styled.Input
              type="text"
              data-test="in-text-identifiers-input"
              value={identifiersValue}
              onChange={(e) => {
                setIdentifiers(e.target.value)
              }}
            />
          </Styled.TextField>
        </Styled.InputContainer>
      </Styled.FormContainer>
      <Styled.PreviewContainer data-test="preview-field">
        <Styled.SubHeader>Preview</Styled.SubHeader>
        <Styled.PreviewContent>
          <div
            dangerouslySetInnerHTML={{ __html: sanitizeHtmlWithFilter(inTextPreview) }}
          />
          <Styled.Button
            onClick={() => handleCopy(inTextPreview)}
            data-test="preview-copy-btn"
          >
            {copied ? 'Copied' : 'Copy'}
          </Styled.Button>
        </Styled.PreviewContent>
      </Styled.PreviewContainer>
      {/* TODO: update styleUrl to be site specific */}
      {styleUrl && (
        <Styled.LargeLink href={styleUrl} target="_blank">
          {style.toUpperCase()} parenthetical rules
          <Styled.Chevron name="chevronRight" />
        </Styled.LargeLink>
      )}
    </div>
  )
}

export default InTextModal
